import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ReactTable, Link, NoContent, ControlledTooltips } from '../../../components/shared';
import { COLUMNS } from '../../../constants/tableColumns';
import { routeConstants } from '../../../constants/routeConstants';
import { useGetAllCollectionsQuery } from '../../../services/CollectionService/CollectionService';
import { COLLECTION_RECONCILIATION_APPROVED } from '../../../constants/CommonConstants';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { getControlledTooltipValueAndCellValue, roundOffValue } from '../../../utils/common-methods';
import { useDispatch } from 'react-redux';
import { setApprovedCollectionFilters } from '../../../redux-slice/collectionFilters';
import { TableSortLabel, Box } from '@mui/material';
import { useSelector } from 'react-redux';

const ApprovedCollectionTable = ({ searchParams, approvedCollection }) => {
  const [currentPage, setCurrentPage] = useState(approvedCollection?.pageNumber);
  const [perPageNumber, setPerPageNumber] = useState(approvedCollection?.rowsPerPage);
  const dispatch = useDispatch();
  const { sort } = useSelector((state) => state.collectionFilter.approvedCollection);

  const viewProductTypes = useCallback((productTypeNames) => {
    const { tooltipValue, cellValue } = getControlledTooltipValueAndCellValue(productTypeNames);
    return <ControlledTooltips tooltipValue={tooltipValue}>{cellValue}</ControlledTooltips>;
  }, []);

  const handleSortChange = (column) => {
    const columnConfig = COLUMNS.APPROVED_COLLECTION_TABLE.find((col) => col.accessor === column);
    if (!columnConfig?.sortKey) return;

    const isDesc = sort?.startsWith(columnConfig?.sortKey) && sort?.endsWith('DESC');
    const newSort = `${columnConfig?.sortKey}:${isDesc ? 'ASC' : 'DESC'}`;

    dispatch(setApprovedCollectionFilters({ ...approvedCollection, sort: newSort, pageNumber: 1 }));
  };

  const transactionLink = useCallback(
    (id, name) => (
      <Link underline="none" to={`${routeConstants.APPROVED_COLLECTION_ROUTE}/${id}`}>
        {name}
      </Link>
    ),
    [],
  );

  const {
    currentData: latestData,
    isError,
    isSuccess,
    isLoading,
    isFetching,
    error,
  } = useGetAllCollectionsQuery({
    searchParams,
    page: currentPage,
    perPage: perPageNumber,
    status: COLLECTION_RECONCILIATION_APPROVED,
    sort,
  });

  const tableData = useMemo(() => {
    if (isSuccess && latestData) {
      const rawData = latestData?.collections;
      return rawData.map((data) => {
        const transformedData = { ...data };
        transformedData.locationName = transactionLink(
          data.transactionId,
          data.location?.locationName ?? data.locationName ?? 'N/A',
        );
        transformedData.locationType = data.location?.locationType?.name ?? data.locationType ?? 'N/A';
        transformedData.locationRevenue = roundOffValue(data.locationRevenue ?? data.totalRevenue);
        transformedData.serviceRep =
          data.serviceRep ?? (data.user ? `${data.user.firstName} ${data.user.lastName}` : 'N/A');

        // Map productCategories array to productTypes
        if (data.productCategories && Array.isArray(data.productCategories) && data.productCategories.length > 0) {
          const productTypeNames = data.productCategories.map((pc) => pc.name);
          transformedData.productTypes = viewProductTypes(productTypeNames);
        } else {
          transformedData.productTypes = viewProductTypes(
            data.productTypes ?? data.productType?.map((pt) => pt?.name) ?? [],
          );
        }

        // Map IndependentRepUsers array to independentRep names
        if (
          data.IndependentRepUsers &&
          Array.isArray(data.IndependentRepUsers) &&
          data.IndependentRepUsers.length > 0
        ) {
          const repNames = data.IndependentRepUsers.map((rep) =>
            `${rep.userDetails?.firstName || ''} ${rep.userDetails?.lastName || ''}`.trim(),
          ).filter((name) => name);
          if (repNames.length > 0) {
            const { tooltipValue, cellValue } = getControlledTooltipValueAndCellValue(repNames);
            transformedData.independentRep = (
              <ControlledTooltips tooltipValue={tooltipValue}>{cellValue}</ControlledTooltips>
            );
          } else {
            transformedData.independentRep = 'N/A';
          }
        } else if (Array.isArray(data.independentRep) && data.independentRep[0] !== 'N/A') {
          const { tooltipValue, cellValue } = getControlledTooltipValueAndCellValue(data.independentRep);
          transformedData.independentRep = (
            <ControlledTooltips tooltipValue={tooltipValue}>{cellValue}</ControlledTooltips>
          );
        } else {
          transformedData.independentRep = 'N/A';
        }

        transformedData.submissionDate = data.submissionDate ?? data.submittedDate ?? 'N/A';

        return transformedData;
      });
    }
  }, [isSuccess, latestData, transactionLink, viewProductTypes]);

  const totalCount = useMemo(() => {
    if (isSuccess && latestData) {
      return latestData?.pagination?.total ?? 0;
    }
    return 0;
  }, [isSuccess, latestData]);

  useEffect(() => {
    setCurrentPage(approvedCollection?.pageNumber);
    setPerPageNumber(approvedCollection?.rowsPerPage);
  }, [searchParams, sort]);

  const pageChangeHandler = (currentPage) => {
    dispatch(
      setApprovedCollectionFilters({ ...approvedCollection, pageNumber: currentPage, rowsPerPage: perPageNumber }),
    );
    setCurrentPage(currentPage);
  };

  const perPageChangeHandler = (newPerPage) => {
    dispatch(setApprovedCollectionFilters({ ...approvedCollection, rowsPerPage: newPerPage, pageNumber: currentPage }));
    setPerPageNumber(newPerPage);
  };

  const approvedCollectionColumns = useMemo(
    () =>
      COLUMNS.APPROVED_COLLECTION_TABLE.map((col) => {
        if (col.sortKey) {
          return {
            ...col,
            Header: () => (
              <Box>
                <TableSortLabel
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      fontSize: '15px',
                      stroke: '#3c3c3c',
                      fill: '#3c3c3c',
                      marginLeft: '10px',
                      marginBottom: '1px',
                    },
                  }}
                  active={sort?.startsWith(col?.sortKey)}
                  direction={sort?.endsWith('DESC') ? 'desc' : 'asc'}
                  onClick={() => handleSortChange(col.accessor)}
                >
                  {col.Header}
                </TableSortLabel>
              </Box>
            ),
          };
        }
        return col;
      }),
    [sort],
  );

  return (
    <>
      {(isLoading || isFetching) && <LoadingSpinner containerHeight={300} />}
      {isError && (
        <NoContent
          title="No Collection Found"
          desc={(isError && error?.data?.message) || <>There are no collection present with this filter.</>}
        />
      )}
      {!isLoading && !isError && !isFetching && tableData && (
        <ReactTable
          data={tableData}
          columns={approvedCollectionColumns}
          pagination
          manualPagination
          totalCount={totalCount}
          pageNumberForTablePagination={currentPage - 1}
          onPageChange={pageChangeHandler}
          onPerPageChange={perPageChangeHandler}
          perPage={perPageNumber}
        />
      )}
    </>
  );
};

export default ApprovedCollectionTable;
